import {
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type KeyboardEvent,
  type ReactNode,
} from "react";

import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card";
import { cn } from "@/lib/utils";
import type {
  AssetMediaRef,
  TestedAsset,
  TestedAssetType,
} from "@digital-twins/shared-types";
import type { AssetMentionBindingTable } from "@digital-twins/shared-types";
import {
  assetMentionLabel,
  bindAssetMentionTokens,
  productVersionLabel,
  respondentWordingFor,
  splitAssetRefTokens,
} from "@digital-twins/shared-types";

import {
  getQuestionnaireMentionDisplayLabel,
  type AssetMentionTables,
} from "../assets/conceptAssets";

export interface ConceptMentionTrigger {
  start: number;
  end: number;
  query: string;
}

function assetMediaUrl(media: AssetMediaRef | undefined) {
  return media?.publicUrl || media?.url || media?.filePath || "";
}

/**
 * Best-effort still image for an @-mention hover card.
 * Images (and materialized PDF/video slices) only — never async PDF render.
 */
export function getAssetMentionPreviewImageUrl(
  asset: TestedAsset,
): string | null {
  const sliceImage = asset.media.find(
    (media) => media.kind === "image" && media.sliceOf && assetMediaUrl(media),
  );
  if (sliceImage) {
    return assetMediaUrl(sliceImage);
  }

  const image = asset.media.find(
    (media) => media.kind === "image" && assetMediaUrl(media),
  );
  if (image) {
    return assetMediaUrl(image);
  }

  const imageByMime = asset.media.find(
    (media) =>
      Boolean(assetMediaUrl(media)) &&
      (media.mimeType ?? "").toLowerCase().startsWith("image/"),
  );
  if (imageByMime) {
    return assetMediaUrl(imageByMime);
  }

  return null;
}

function assetTypePreviewLabel(type: TestedAssetType) {
  const spaced = String(type).replace(/_/g, " ");
  return `${spaced.charAt(0).toUpperCase()}${spaced.slice(1)}`;
}

interface AssetMentionPreviewContentProps {
  title: string;
  assetType: TestedAssetType;
  imageUrl: string | null;
}

export function AssetMentionPreviewContent(
  props: AssetMentionPreviewContentProps,
) {
  const { title, assetType, imageUrl } = props;

  return (
    <div
      className={cn("flex items-start gap-2.5", imageUrl ? "min-w-[168px]" : "min-w-0")}
      data-testid="concept-mention-preview"
    >
      {imageUrl ? (
        <div className="h-[54px] w-[72px] shrink-0 overflow-hidden rounded-[6px] bg-muted">
          <img
            src={imageUrl}
            alt=""
            className="h-full w-full object-cover"
            data-testid="concept-mention-preview-image"
          />
        </div>
      ) : null}
      <div className="min-w-0 py-0.5">
        <p
          className="truncate text-[13px] font-medium leading-tight text-foreground"
          data-testid="concept-mention-preview-title"
        >
          {title}
        </p>
        <p
          className="mt-0.5 text-[11px] leading-tight text-muted-foreground"
          data-testid="concept-mention-preview-type"
        >
          {assetTypePreviewLabel(assetType)}
        </p>
      </div>
    </div>
  );
}

interface AssetMentionChipProps {
  displayName: string;
  concept: TestedAsset;
}

function AssetMentionChip(props: AssetMentionChipProps) {
  const { displayName, concept } = props;
  const imageUrl = getAssetMentionPreviewImageUrl(concept);
  const previewTitle = concept.name.trim() || displayName;

  return (
    <HoverCard openDelay={200} closeDelay={100}>
      <HoverCardTrigger asChild>
        <span
          className="rounded-[5px] bg-[#EEE4FF] px-1.5 py-0.5 font-medium text-[#7C3AED]"
          data-asset-key={concept.stableKey}
          data-concept-mention="true"
          data-testid="concept-mention-chip"
        >
          @{displayName}
        </span>
      </HoverCardTrigger>
      <HoverCardContent
        side="top"
        align="start"
        sideOffset={6}
        className="w-auto max-w-[260px] border-border p-2.5 shadow-md"
        data-testid="concept-mention-preview-content"
      >
        <AssetMentionPreviewContent
          title={previewTitle}
          assetType={concept.type}
          imageUrl={imageUrl}
        />
      </HoverCardContent>
    </HoverCard>
  );
}


function textNodeWalker(root: HTMLElement) {
  const nodeFilter =
    root.ownerDocument.defaultView?.NodeFilter.SHOW_TEXT ?? 4;
  return root.ownerDocument.createTreeWalker(root, nodeFilter);
}

export function plainTextFromEditable(root: HTMLElement) {
  return (root.textContent ?? "").replace(/\u00a0/g, " ");
}

export function getPlainTextSelectionOffset(root: HTMLElement) {
  const selection = root.ownerDocument.getSelection();
  if (!selection || selection.rangeCount === 0) {
    return plainTextFromEditable(root).length;
  }
  const range = selection.getRangeAt(0);
  if (!root.contains(range.startContainer)) {
    return plainTextFromEditable(root).length;
  }
  const prefixRange = range.cloneRange();
  prefixRange.selectNodeContents(root);
  prefixRange.setEnd(range.startContainer, range.startOffset);
  return prefixRange.toString().replace(/\u00a0/g, " ").length;
}

export function setPlainTextSelectionOffset(
  root: HTMLElement,
  offset: number,
) {
  root.focus();
  const selection = root.ownerDocument.getSelection();
  if (!selection) return;

  const safeOffset = Math.max(0, offset);
  const walker = textNodeWalker(root);
  let remaining = safeOffset;
  let node = walker.nextNode();
  let lastTextNode: Text | null = null;

  while (node) {
    const textNode = node as Text;
    const length = textNode.textContent?.length ?? 0;
    lastTextNode = textNode;
    if (remaining <= length) {
      const range = root.ownerDocument.createRange();
      range.setStart(textNode, remaining);
      range.collapse(true);
      selection.removeAllRanges();
      selection.addRange(range);
      return;
    }
    remaining -= length;
    node = walker.nextNode();
  }

  const range = root.ownerDocument.createRange();
  if (lastTextNode) {
    range.setStart(lastTextNode, lastTextNode.textContent?.length ?? 0);
  } else {
    range.setStart(root, 0);
  }
  range.collapse(true);
  selection.removeAllRanges();
  selection.addRange(range);
}

export function getConceptMentionTrigger(
  value: string,
  caretIndex: number,
): ConceptMentionTrigger | null {
  const safeCaret = Math.max(0, Math.min(caretIndex, value.length));
  const beforeCaret = value.slice(0, safeCaret);
  const start = beforeCaret.lastIndexOf("@");
  if (start < 0) {
    return null;
  }
  const query = beforeCaret.slice(start + 1);
  if (query.includes("\n") || query.endsWith(" ") || query.length > 80) {
    return null;
  }
  return {
    start,
    end: safeCaret,
    query: query.trimStart(),
  };
}

export function filterConceptMentionSuggestions(
  concepts: Array<TestedAsset>,
  query: string,
) {
  const normalizedQuery = query.trim().toLowerCase();
  return concepts
    .filter((concept) => concept.name.trim().length > 0)
    .filter((concept) => {
      if (!normalizedQuery) return true;
      return concept.name.toLowerCase().includes(normalizedQuery);
    })
    .slice(0, 6);
}

export function insertConceptMentionText(
  value: string,
  trigger: ConceptMentionTrigger,
  conceptName: string,
) {
  const mention = `@${conceptName.trim()}`;
  const suffix = value.slice(trigger.end);
  const needsSpace = suffix.length === 0 || /^[^\s.,!?;:)]/.test(suffix);
  const inserted = `${mention}${needsSpace ? " " : ""}`;
  return {
    value: `${value.slice(0, trigger.start)}${inserted}${suffix}`,
    caretIndex: trigger.start + inserted.length,
  };
}

/** The name `table` gave the asset with `stableKey`, or "". */
function tableNameOfKey(table: AssetMentionBindingTable, stableKey: string) {
  for (const [assetId, key] of table.keyByAssetId) {
    if (key === stableKey) return table.nameByAssetId.get(assetId) ?? "";
  }
  return "";
}

/**
 * The field's text with each mention drawn as a chip, by the Save's own rule
 * (C23-f): a mention binds against the saved catalog first, then the pending
 * one (`bindAssetMentionTokens`), so a mention written before an unsaved
 * rename still names its asset. The chip draws that asset's pending record
 * through the shared-name rule (`assetMentionLabel`). A mention of an asset
 * the pending catalog no longer holds reads as plain `@Name` text. The
 * field's value, the `@Name` text, is never changed here (D2 S3.1).
 */
export function renderMentionContent(
  value: string,
  tables: AssetMentionTables,
  concepts: Array<TestedAsset>,
  /**
   * The one tested asset the field stands for (a grid row or an answer bound
   * to it by its `assetRefs`, or the price version a question's title and
   * description are about, `ownPriceVersion`). It wins a mention its name
   * shares, and its chip names it by its version label ("name · price"):
   * price versions share the product's name, and the label tells them apart
   * (F26 #16). A mention in free text names the product, except in a
   * question about one price version, which names that version (D9).
   */
  boundAssetId?: string,
  /**
   * Display only: the text respondents read for the bound asset (a tested-
   * assets grid row); its chip shows it in place of the asset's name. The
   * field's value, the `@Name` mention, is unchanged.
   */
  boundAssetLabel?: string,
  /** The draft's content language: a price's period reads in it. */
  contentLanguage?: string,
) {
  if (value.length === 0) {
    return null;
  }
  const bound = boundAssetId
    ? concepts.find((concept) => concept.id === boundAssetId)
    : undefined;
  const ownKey = bound?.stableKey;
  const tokenized = bindAssetMentionTokens(
    bindAssetMentionTokens(value, tables.saved, ownKey),
    tables.live,
    ownKey,
  );
  const byKey = new Map(concepts.map((concept) => [concept.stableKey, concept]));
  const nodes: Array<ReactNode> = [];
  for (const piece of splitAssetRefTokens(tokenized)) {
    if (typeof piece === "string") {
      nodes.push(piece);
      continue;
    }
    const concept = byKey.get(piece.stableKey);
    if (!concept) {
      nodes.push(`@${tableNameOfKey(tables.saved, piece.stableKey)}`);
      continue;
    }
    // The assets that share its name (price versions of one product).
    const holders = (
      tables.live.keysByName.get(concept.name.trim().toLowerCase()) ?? [
        piece.stableKey,
      ]
    ).flatMap((key) => byKey.get(key) ?? []);
    const displayName = assetMentionLabel(
      holders.length > 0 ? holders : [concept],
      bound ? new Set([bound.id]) : undefined,
      (holder) => getQuestionnaireMentionDisplayLabel(holder),
      (holder) =>
        boundAssetLabel ??
        productVersionLabel(
          getQuestionnaireMentionDisplayLabel(holder),
          holder.type === "product" ? holder.price : undefined,
          respondentWordingFor(contentLanguage),
        ),
    );
    nodes.push(
      <AssetMentionChip
        key={`${nodes.length}-${piece.stableKey}`}
        displayName={displayName}
        concept={concept}
      />,
    );
  }
  return nodes.length > 0 ? nodes : value;
}

interface ConceptMentionFieldProps {
  "data-testid"?: string;
  /**
   * The field's name for assistive technology, in the UI language. Required:
   * a contenteditable textbox has no placeholder fallback, so without it a
   * screen reader announces "edit text" alone (E-session-13).
   */
  ariaLabel: string;
  /**
   * The one tested asset this field stands for (a grid row or an answer
   * bound to it, or the price version a question is about); its chip names
   * it by its version label.
   */
  boundAssetId?: string;
  /** Display only: the bound asset's respondent text (`renderMentionContent`). */
  boundAssetLabel?: string;
  className?: string;
  /** The draft's content language: a chip's price reads in it. */
  contentLanguage?: string;
  /** The pending catalog: its records draw the chips and its names are offered. */
  conceptAssets: Array<TestedAsset>;
  /** The saved and pending catalogs' mention tables (`AssetMentionTables`). */
  mentionTables: AssetMentionTables;
  disabled?: boolean;
  onChange: (value: string) => void;
  placeholder?: string;
  rows?: number;
  singleLine?: boolean;
  /** Tooltip, e.g. why a read-only field cannot be edited here. */
  title?: string;
  value: string;
}

function scheduleSelectionRestore(callback: () => void) {
  if (typeof window !== "undefined" && window.requestAnimationFrame) {
    window.requestAnimationFrame(callback);
    return;
  }
  window.setTimeout(callback, 0);
}

export function ConceptMentionField(props: ConceptMentionFieldProps) {
  const {
    "data-testid": testId,
    ariaLabel,
    boundAssetId,
    boundAssetLabel,
    className,
    conceptAssets,
    contentLanguage,
    disabled,
    mentionTables,
    onChange,
    placeholder,
    rows = 1,
    singleLine,
    title,
    value,
  } = props;
  const editableRef = useRef<HTMLDivElement>(null);
  const pendingSelectionOffsetRef = useRef<number | null>(null);
  const [caretIndex, setCaretIndex] = useState(value.length);
  const [activeIndex, setActiveIndex] = useState(0);
  const [isFocused, setIsFocused] = useState(false);
  const trigger = useMemo(
    () => getConceptMentionTrigger(value, caretIndex),
    [caretIndex, value],
  );
  const suggestions = useMemo(
    () =>
      trigger
        ? filterConceptMentionSuggestions(conceptAssets, trigger.query)
        : [],
    [conceptAssets, trigger],
  );
  const showSuggestions = isFocused && !disabled && suggestions.length > 0;

  useLayoutEffect(() => {
    const root = editableRef.current;
    if (!root) return;

    const pendingOffset = pendingSelectionOffsetRef.current;
    if (pendingOffset !== null) {
      pendingSelectionOffsetRef.current = null;
      if (root.ownerDocument.activeElement === root) {
        setPlainTextSelectionOffset(root, Math.min(pendingOffset, value.length));
      }
    }

    if (isInternalChangeRef.current) {
      isInternalChangeRef.current = false;
      return;
    }
  }, [isFocused, value]);

  const updateCaretFromSelection = () => {
    const root = editableRef.current;
    if (!root) return;
    if (!disabled) {
      setIsFocused(true);
    }
    setCaretIndex(getPlainTextSelectionOffset(root));
  };

  const isInternalChangeRef = useRef(false);

  const emitValueFromEditable = (root: HTMLElement) => {
    const rawValue = plainTextFromEditable(root);
    const nextValue = singleLine ? rawValue.replace(/\n+/g, " ") : rawValue;
    const nextCaret = Math.min(
      getPlainTextSelectionOffset(root),
      nextValue.length,
    );
    pendingSelectionOffsetRef.current = nextCaret;
    setCaretIndex(nextCaret);
    setActiveIndex(0);
    isInternalChangeRef.current = true;
    onChange(nextValue);
  };

  const insertConcept = (concept: TestedAsset) => {
    const root = editableRef.current;
    const currentCaret = root ? getPlainTextSelectionOffset(root) : caretIndex;
    const currentTrigger = getConceptMentionTrigger(value, currentCaret);
    if (!currentTrigger) return;
    const next = insertConceptMentionText(
      value,
      currentTrigger,
      getQuestionnaireMentionDisplayLabel(concept),
    );
    pendingSelectionOffsetRef.current = next.caretIndex;
    onChange(next.value);
    setActiveIndex(0);
    setCaretIndex(next.caretIndex);
    scheduleSelectionRestore(() => {
      const editable = editableRef.current;
      if (!editable) return;
      setPlainTextSelectionOffset(editable, next.caretIndex);
    });
  };

  const liveRenderKey = `${value}::${conceptAssets
    .map((asset) => `${asset.id}:${asset.name}`)
    .join("|")}`;
  const frozenRenderKeyRef = useRef(liveRenderKey);
  if (!isFocused) {
    frozenRenderKeyRef.current = liveRenderKey;
  }
  const renderKey = frozenRenderKeyRef.current;

  return (
    <div className="relative w-full" title={title}>
      <div
        // Rebuilding the node is only ever needed when something OUTSIDE the
        // field changed what it should show — an asset renamed or removed, or
        // the value rewritten by the chat — because the author's own typing
        // leaves React's idea of the children behind the real contentEditable
        // DOM. It must NEVER happen under a live caret: keying on `value`
        // alone remounts on every keystroke (only the first character lands),
        // and keying on focus remounts the node the click just focused. So the
        // key tracks while the field is idle and freezes while it has focus;
        // blur unfreezes it and the DOM is rebuilt clean.
        key={renderKey}
        ref={editableRef}
        aria-label={ariaLabel}
        aria-multiline={!singleLine || rows > 1}
        className={cn(
          "block w-full whitespace-pre-wrap break-words rounded-md border border-input bg-background px-3 py-2 text-sm text-foreground ring-offset-background empty:before:pointer-events-none empty:before:text-muted-foreground empty:before:content-[attr(data-placeholder)] focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-50",
          className,
        )}
        contentEditable={!disabled}
        data-placeholder={placeholder ?? ""}
        data-testid={testId}
        onBlur={() => {
          setIsFocused(false);
          isInternalChangeRef.current = false;
        }}
        onClick={updateCaretFromSelection}
        onFocus={() => {
          // A read-only field never enters the editing render: it swaps chips
          // for raw "@Name" text the author cannot edit their way out of, so a
          // locked matrix row reads as though it lost its asset. `tabIndex`
          // below keeps a click from focusing it at all; this guard covers
          // programmatic focus.
          if (disabled) {
            return;
          }
          // Focus swaps the chips for raw text so the caret has something to
          // sit in. That replaces the children under a live caret, so remember
          // where it was and put it back once the swap has rendered.
          const root = editableRef.current;
          if (root) {
            pendingSelectionOffsetRef.current =
              getPlainTextSelectionOffset(root);
          }
          setIsFocused(true);
          updateCaretFromSelection();
        }}
        onInput={(event: FormEvent<HTMLDivElement>) => {
          emitValueFromEditable(event.currentTarget);
        }}
        onKeyDown={(event: KeyboardEvent<HTMLDivElement>) => {
          if (showSuggestions && event.key === "ArrowDown") {
            event.preventDefault();
            setActiveIndex((current) => (current + 1) % suggestions.length);
            return;
          }
          if (showSuggestions && event.key === "ArrowUp") {
            event.preventDefault();
            setActiveIndex(
              (current) =>
                (current - 1 + suggestions.length) % suggestions.length,
            );
            return;
          }
          if (
            showSuggestions &&
            (event.key === "Enter" || event.key === "Tab")
          ) {
            event.preventDefault();
            insertConcept(suggestions[activeIndex] ?? suggestions[0]);
            return;
          }
          if (showSuggestions && event.key === "Escape") {
            event.preventDefault();
            setIsFocused(false);
            return;
          }
          if (singleLine && event.key === "Enter") {
            event.preventDefault();
          }
        }}
        onKeyUp={updateCaretFromSelection}
        onSelect={updateCaretFromSelection}
        role="textbox"
        spellCheck
        suppressContentEditableWarning
        // No tabIndex at all when read-only: `tabIndex={-1}` still lets a click
        // focus the div (it only removes it from the tab order), which is what
        // used to knock a locked row out of its chip rendering.
        tabIndex={disabled ? undefined : 0}
      >
        {!isFocused
          ? renderMentionContent(
              value,
              mentionTables,
              conceptAssets,
              boundAssetId,
              boundAssetLabel,
              contentLanguage,
            )
          : (value || null)}
      </div>
      {showSuggestions ? (
        <div className="absolute left-0 top-full z-40 mt-1 max-h-56 min-w-[240px] overflow-y-auto rounded-[8px] border border-border bg-background p-1 shadow-[0_8px_28px_rgba(7,18,15,0.16)]">
          {suggestions.map((concept, index) => (
            <button
              key={concept.id}
              type="button"
              className={cn(
                "flex w-full items-center rounded-[6px] px-2.5 py-2 text-left text-[12.5px] font-medium",
                index === activeIndex
                  ? "bg-[#EEE4FF] text-[#7C3AED]"
                  : "text-foreground hover:bg-muted",
              )}
              onMouseDown={(event) => {
                event.preventDefault();
                insertConcept(concept);
              }}
            >
              @{getQuestionnaireMentionDisplayLabel(concept)}
            </button>
          ))}
        </div>
      ) : null}
    </div>
  );
}
